/**
 * Feb 2026 R293zd · CreateInvoiceDialog — mirror of CreateSalesOrderDialog
 * but one step down the money path (SO → Invoice). Picks line items +
 * partial quantities. Server guards against over-billing; this dialog
 * shows live remaining qty next to each input so the user doesn't fight
 * a 422.
 *
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   ws         workspace id
 *   so         the sales-order document the invoice is being spawned from
 *   onCreated  (invoice) => void   fires after the server returns the new INV
 */

import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Receipt, ArrowRight, FileText, CheckCircle2, Loader2 } from "lucide-react";
import { fmtMoney } from "@/lib/currency";
import FulfilmentBars from "./FulfilmentBars";

export default function CreateInvoiceDialog({ open, onClose, ws, so, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [fulfil, setFulfil] = useState(null);
  // Picks keyed by line id → { qty, rate_override? }
  const [picks, setPicks] = useState({});
  const [docDate, setDocDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [subject, setSubject] = useState("");

  useEffect(() => {
    if (!open || !so?.id || !ws) return;
    let cancelled = false;
    setLoading(true);
    setPicks({});
    setSubject(so.subject || "");
    (async () => {
      try {
        const r = await api.get(`/gst/workspaces/${ws}/sales-orders/${so.id}/invoice-fulfillment`);
        if (cancelled) return;
        setFulfil(r.data);
      } catch (e) {
        toast.error(apiErrorMessage(e, "Could not load SO fulfilment"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, so?.id, ws]);

  const lines = fulfil?.lines || [];

  const totals = useMemo(() => {
    let taxable = 0;
    let gst = 0;
    for (const l of lines) {
      const p = picks[l.id];
      if (!p?.qty) continue;
      const q = Number(p.qty) || 0;
      const rate = p.rate_override != null ? Number(p.rate_override) : l.rate;
      const sub = q * rate;
      taxable += sub;
      gst += (sub * (l.gst_rate || 0)) / 100;
    }
    return { taxable, gst, grand: taxable + gst };
  }, [lines, picks]);

  const picksArr = useMemo(() => Object.entries(picks)
    .filter(([, v]) => Number(v?.qty) > 0)
    .map(([id, v]) => ({
      sales_order_line_id: id,
      qty: Number(v.qty),
      rate: v.rate_override != null && v.rate_override !== "" ? Number(v.rate_override) : null,
    })), [picks]);

  const anyOver = useMemo(() => {
    for (const l of lines) {
      const p = picks[l.id];
      if (!p?.qty) continue;
      if (Number(p.qty) > l.qty_remaining + 1e-6) return true;
    }
    return false;
  }, [lines, picks]);

  const submit = async () => {
    if (!picksArr.length) {
      toast.error("Pick at least one line");
      return;
    }
    if (anyOver) {
      toast.error("One or more quantities exceed the remaining qty");
      return;
    }
    setPosting(true);
    try {
      const r = await api.post(
        `/gst/workspaces/${ws}/sales-orders/${so.id}/invoices`,
        {
          doc_date: docDate,
          due_date: dueDate || null,
          subject: subject || null,
          picks: picksArr,
        }
      );
      toast.success(`Invoice ${r.data.doc_no} created`);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not create invoice"));
    } finally {
      setPosting(false);
    }
  };

  const quickFillRemaining = () => {
    const next = {};
    for (const l of lines) {
      if (l.qty_remaining > 0) next[l.id] = { qty: l.qty_remaining };
    }
    setPicks(next);
  };

  if (!so) return null;
  const allFulfilled = !!fulfil?.all_fulfilled;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="w-screen max-w-full h-[100dvh] p-0 flex flex-col sm:max-w-3xl sm:h-auto sm:max-h-[90vh]"
        data-testid="create-invoice-dialog"
      >
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Receipt className="h-4 w-4" />
            Create invoice from {so.doc_no}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pick line items and quantities to bill. Partial quantities OK — the SO can bill out in multiple invoices.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading SO lines…
            </div>
          )}

          {!loading && allFulfilled && (
            <div className="border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 rounded p-3 text-sm text-emerald-800 dark:text-emerald-200 flex items-center gap-2"
                 data-testid="create-inv-all-fulfilled">
              <CheckCircle2 className="h-4 w-4" />
              Every line on this SO has been fully invoiced.
            </div>
          )}

          {!loading && !allFulfilled && lines.length > 0 && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-muted-foreground">
                  {fulfil?.child_invoices?.length ? `${fulfil.child_invoices.length} invoice(s) already issued from this SO.` : "First invoice from this SO."}
                </div>
                <button
                  type="button"
                  onClick={quickFillRemaining}
                  className="text-[11px] uppercase tracking-widest px-2 py-1 border border-border rounded hover:bg-muted"
                  data-testid="create-inv-fill-remaining"
                >
                  Bill everything remaining
                </button>
              </div>

              {/* Feb 2026 R293ze · Visual fulfilment bars so the user
                  sees at a glance how much is left to bill before
                  picking quantities. */}
              {fulfil?.lines?.length > 0 && (
                <div className="border border-border rounded p-3 bg-muted/10"
                     data-testid="create-inv-fulfil-overview">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                    SO fulfilment · what's left to bill
                  </div>
                  <FulfilmentBars lines={fulfil.lines} fulfilledKey="qty_invoiced"
                                   testid="create-inv-fulfil-bars" />
                </div>
              )}

              <div className="space-y-2" data-testid="create-inv-lines">
                {lines.map((l) => {
                  const p = picks[l.id] || {};
                  const q = Number(p.qty) || 0;
                  const over = q > l.qty_remaining + 1e-6;
                  const disabled = l.qty_remaining <= 0;
                  const rate = p.rate_override != null && p.rate_override !== "" ? Number(p.rate_override) : l.rate;
                  const sub = q * rate;
                  return (
                    <div
                      key={l.id}
                      className={`border rounded p-2 sm:p-3 ${disabled ? "opacity-50 bg-muted/20" : "bg-background"} ${over ? "border-rose-500" : "border-border"}`}
                      data-testid={`create-inv-line-${l.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{l.description}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {l.hsn_sac && <>HSN {l.hsn_sac} · </>}
                            SO qty <b>{l.qty}</b> · Billed <b>{l.qty_invoiced}</b> · Remaining <b className={l.qty_remaining > 0 ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}>{l.qty_remaining}</b>
                          </div>
                        </div>
                        <div className="text-right text-xs">
                          <div className="font-mono text-foreground">{fmtMoney(sub)}</div>
                          <div className="text-[10px] text-muted-foreground">+ GST {l.gst_rate}%</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Bill qty</Label>
                          <Input
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            max={l.qty_remaining}
                            disabled={disabled}
                            value={p.qty ?? ""}
                            onChange={(e) => setPicks((cur) => ({
                              ...cur,
                              [l.id]: { ...(cur[l.id] || {}), qty: e.target.value },
                            }))}
                            placeholder={`max ${l.qty_remaining}`}
                            className={`h-10 rounded-none ${over ? "border-rose-500 focus-visible:ring-rose-500" : ""}`}
                            data-testid={`create-inv-qty-${l.id}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Rate (optional)</Label>
                          <Input
                            type="number"
                            inputMode="decimal"
                            disabled={disabled}
                            value={p.rate_override ?? ""}
                            onChange={(e) => setPicks((cur) => ({
                              ...cur,
                              [l.id]: { ...(cur[l.id] || {}), rate_override: e.target.value },
                            }))}
                            placeholder={`${l.rate}`}
                            className="h-10 rounded-none"
                            data-testid={`create-inv-rate-${l.id}`}
                          />
                        </div>
                      </div>
                      {over && (
                        <div className="text-[11px] text-rose-700 dark:text-rose-300 mt-1">
                          Over-billing: {q} &gt; remaining {l.qty_remaining}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-border pt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Invoice date</Label>
                  <Input
                    type="date"
                    value={docDate}
                    onChange={(e) => setDocDate(e.target.value)}
                    className="h-10 rounded-none"
                    data-testid="create-inv-doc-date"
                  />
                </div>
                <div>
                  <Label className="text-xs">Due date</Label>
                  <Input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="h-10 rounded-none"
                    data-testid="create-inv-due-date"
                  />
                </div>
                <div>
                  <Label className="text-xs">Subject</Label>
                  <Input
                    type="text"
                    maxLength={120}
                    placeholder={so.subject || "e.g. Milestone 1 — delivery"}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="h-10 rounded-none"
                    data-testid="create-inv-subject"
                  />
                </div>
              </div>

              <div className="border border-border rounded p-3 bg-muted/20 flex items-center justify-between flex-wrap gap-2"
                   data-testid="create-inv-totals">
                <div className="text-xs text-muted-foreground">
                  {picksArr.length} line{picksArr.length === 1 ? "" : "s"} selected
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Est. invoice total</div>
                  <div className="text-base font-semibold">
                    {fmtMoney(totals.grand)}
                    <span className="text-[11px] font-normal text-muted-foreground ml-1">
                      ({fmtMoney(totals.taxable)} + {fmtMoney(totals.gst)} GST)
                    </span>
                  </div>
                </div>
              </div>

              {fulfil?.child_invoices?.length > 0 && (
                <div className="border border-dashed border-border rounded p-2 text-[11px] text-muted-foreground"
                     data-testid="create-inv-history">
                  <div className="font-medium text-foreground mb-1 flex items-center gap-1">
                    <FileText className="h-3 w-3" /> Already invoiced
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {fulfil.child_invoices.map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border bg-background">
                        {c.doc_no}
                        <span className="uppercase tracking-wide text-[9px] text-muted-foreground">{c.status}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-4 sm:px-6 py-3 border-t border-border flex flex-row justify-between gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="create-inv-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={posting || loading || !picksArr.length || anyOver}
            data-testid="create-inv-submit"
          >
            {posting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Creating…</>
                     : <>Create invoice <ArrowRight className="h-4 w-4 ml-1" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
